import { useState, useCallback, useEffect } from 'react';
import { signUp, signIn, signOut, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import type { SignUpInput, SignInInput } from '@/types/auth';

interface UseAuthReturn {
  isLoading: boolean;
  error: string | null;
  userEmail: string | null;
  signUp: (input: SignUpInput) => Promise<boolean>;
  signIn: (input: SignInInput) => Promise<boolean>;
  signOut: () => Promise<boolean>;
}

export const useAuth = (): UseAuthReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || user.username);
      } catch {
        setUserEmail(null);
      }
    };
    fetchUser();
  }, []);

  const handleSignUp = useCallback(async (input: SignUpInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const { userId, isSignUpComplete } = await signUp({
        username: input.email,
        password: input.password,
        options: {
          userAttributes: {
            email: input.email
          }
        }
      });
      
      console.log('Signup response:', { userId, isSignUpComplete });
      return true;
    } catch (error: unknown) {
      const err = error as { name: string; message: string; code?: string };
      console.error('Signup error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        fullError: err
      });
      
      if (err.name === 'UsernameExistsException') {
        setError('このメールアドレスは既に登録されています。ログインをお試しいただくか、別のメールアドレスで登録をお願いします。');
        return false;
      }
      
      if (err.name === 'InvalidPasswordException') {
        setError('パスワードは8文字以上で、大文字・小文字の英字、数字を含める必要があります。');
        return false;
      }

      if (err.name === 'InvalidParameterException') {
        setError('入力内容が正しくありません。メールアドレスの形式をご確認ください。');
        return false;
      }

      setError(err.message || '予期せぬエラーが発生しました。しばらく時間をおいて再度お試しください。');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSignIn = useCallback(async (input: SignInInput) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('ログイン開始:', input.email);
      const { isSignedIn } = await signIn({ 
        username: input.email, 
        password: input.password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH"
        }
      });
      console.log('ログイン結果:', isSignedIn);
      return isSignedIn;
    } catch (error: unknown) {
      const err = error as { name: string; message: string; code?: string };
      console.error('ログインエラー詳細:', {  // デバッグ用
        name: err.name,
        message: err.message,
        code: err.code,
        fullError: err
      });
      
      let errorMessage = '予期せぬエラーが発生しました';
      
      if (err.name === 'UserNotFoundException') {
        errorMessage = 'メールアドレスまたはパスワードが正しくありません';
      } else if (err.name === 'NotAuthorizedException') {
        errorMessage = 'メールアドレスまたはパスワードが正しくありません';
      } else if (err.name === 'UserNotConfirmedException') {
        errorMessage = 'メールアドレスの確認が完了していません';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signOut();
      setUserEmail(null);
      window.location.href = '/app';
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    error,
    userEmail,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };
};
